import React from 'react';

import {
	TextField,
	Autocomplete,
	CircularProgress,
	FormHelperText,
	ListItemText,
	Box,
	Stack,
	FormControl,
	FormLabel,
	Typography,
	Divider,
	MenuItem,
	Select,
} from '@mui/material';

import debounce from 'lodash/throttle';
import type { FormikProps } from 'formik';

import type { AddBulkRequestProps } from '../../../models';
import { useGetSuppliersQuery } from '../../../slices/apis/testingCentreApi';

interface BulkRequestLocationProps {
	formik: FormikProps<Pick<AddBulkRequestProps, 'location'>>;
}

interface SuggestResponse {
	ResultItems: {
		Title: string;
	}[];
}

interface Option {
	label: string;
}

export const BulkRequestLocation: React.FC<BulkRequestLocationProps> = ({ formik }) => {
	const [open, setOpen] = React.useState(false);
	const [options, setOptions] = React.useState<Option[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [ausrehabAddr, setAusrehabAddr] = React.useState('');
	const [onsiteAddr, setOnsiteAddr] = React.useState('');

	const { data: testingCentres } = useGetSuppliersQuery();

	const onAusrehabAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setOnsiteAddr('');
		const selectedCentre = testingCentres?.find((centre) => {
			return centre.name === event.target.value;
		});

		if (selectedCentre) {
			setAusrehabAddr(`${selectedCentre.name}: ${selectedCentre.address}`);
		}
	};

	const onOnsiteAddressChange = (address: string) => {
		setOnsiteAddr(address);
		setAusrehabAddr('');
	};

	const fetchLocations = React.useCallback(async (query: string) => {
		if (!query) {
			setOptions([]);
			return;
		}

		setLoading(true);

		try {
			const res = await fetch(
				`https://places.geo.ap-southeast-2.amazonaws.com/v2/suggest?key=${process.env.REACT_APP_PES_LOCATION_SERVICE_API_KEY}`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						QueryText: query,
						BiasPosition: [151.2093, -33.8688],
					}),
				},
			);
			const data = (await res.json()) as SuggestResponse;

			if (data.ResultItems) {
				setOptions(data.ResultItems.map((m) => ({ label: m.Title })));
			} else {
				setOptions([]);
			}
		} catch (err) {
			setOptions([]);
		} finally {
			setLoading(false);
		}
	}, []);

	const throttledFetch = React.useMemo(() => debounce(fetchLocations, 500), [fetchLocations]);

	const ifError = Boolean(formik.touched.location) && Boolean(formik.errors.location);

	React.useEffect(() => {
		return () => throttledFetch.cancel();
	}, [throttledFetch]);

	React.useEffect(() => {
		const newValue = onsiteAddr || ausrehabAddr;
		if (formik.values.location !== newValue) {
			if (newValue) {
				formik.setFieldValue('location', newValue);
			}
		}
	}, [formik, onsiteAddr, ausrehabAddr]);

	return (
		<Stack
			sx={{
				width: {
					sm: '100%',
					md: 450,
				},
			}}
		>
			{ifError && <FormHelperText error>{formik.errors.location as string}</FormHelperText>}
			<FormControl>
				<FormLabel>On-Site</FormLabel>
				<Autocomplete
					open={open}
					onOpen={() => setOpen(true)}
					onClose={() => setOpen(false)}
					options={options}
					loading={loading}
					noOptionsText={null}
					inputValue={onsiteAddr}
					filterOptions={(x) => x}
					// when typing
					onInputChange={(event, value) => {
						// don't fetch when picking an ausrehab clinic address
						if (ausrehabAddr === '') {
							setOnsiteAddr(value);
							throttledFetch(value);
						}
					}}
					// when selecting an address from list
					onChange={(event, value, reason) => {
						if (value?.label) {
							onOnsiteAddressChange(value.label);
						}
					}}
					onFocus={(event) => {
						setAusrehabAddr('');
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loading ? <CircularProgress size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
			</FormControl>
			<Box sx={{ my: 2 }}>
				<Divider textAlign="center">
					<Typography color="grey">OR</Typography>
				</Divider>
			</Box>
			<FormControl>
				<FormLabel>At an Ausrehab location</FormLabel>
				<Select
					labelId="demo-simple-select-label"
					id="demo-simple-select"
					value={ausrehabAddr}
					label=""
					onChange={onAusrehabAddressChange}
				>
					{testingCentres
						?.filter((centre) => {
							return centre.isBulk;
						})
						.map((centre) => {
							return (
								<MenuItem key={centre.id} value={centre.name}>
									<ListItemText primary={centre.name} secondary={centre.address} />
								</MenuItem>
							);
						})}
				</Select>
			</FormControl>
			{formik.values.location && testingCentres && (
				<SelectedLocation
					isAusrehabAddr={testingCentres.some((centre) => {
						return formik.values.location.includes(centre.name);
					})}
					location={formik.values.location}
				/>
			)}
		</Stack>
	);
};

interface SelectedLocationProps {
	isAusrehabAddr: boolean;
	location: string;
}

const SelectedLocation = ({ isAusrehabAddr, location }: SelectedLocationProps) => {
	if (!isAusrehabAddr) {
		return (
			<Box sx={{ mt: 2 }}>
				Your tests will be conducted at:<Typography variant="h6">{location}</Typography>
			</Box>
		);
	}

	const testingCentre = location.split(':');

	return (
		<Stack sx={{ mt: 2 }}>
			<Box>
				Your tests will be conducted at:<Typography variant="h6">{testingCentre[0]}</Typography>
			</Box>
			<Box>
				<Typography variant="subtitle1" color="grey.500">
					{testingCentre[1]}
				</Typography>
			</Box>
			{/* a hack as it's unclear if we want to add a note field to testing centre entity for dynamic config */}
			{testingCentre[0].includes('Blacktown') && (
				<Box>
					<Typography variant="subtitle2" color="error">
						Our Blacktown location can accommodate up to 5 requests per day. For more than 5 requests, we require at
						least one week's advance notice.
					</Typography>
				</Box>
			)}
		</Stack>
	);
};
